import { useCallback, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { format } from 'date-fns';

import {
  Box,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import ReplyIcon from '@mui/icons-material/Reply';
import { ProfilePicture } from 'components/common/ProfilePicture/ProfilePicture';

import {
  candidateDetailsSelectors,
  sendCandidateQuestion,
} from '@redux/candidateDetails';
import { selectPermissions } from '@redux/accounts/accounts.selectors';

import { ICandidateDetails } from '@types';
import { isStatusLoading } from '@utils';
import { hasPermission } from '@constants';

export const CandidateDetailsQuestions: React.FC<{
  data: ICandidateDetails;
}> = ({ data }) => {
  const dispatch = useAppDispatch();
  const canUpdateCandidate = hasPermission(
    useAppSelector(selectPermissions),
    'H:W',
  );
  const { sendCandidateQuestionAPIStatus } = useAppSelector(
    candidateDetailsSelectors.getCandidateDetailsData,
  );

  const [questionText, setQuestionText] = useState('');

  const onSubmitQuestion = useCallback(async () => {
    dispatch(
      sendCandidateQuestion({ candidateId: data.id, text: questionText }),
    );
  }, [questionText, data.id]);

  const isLoading = isStatusLoading(sendCandidateQuestionAPIStatus);

  if (!data.submissionQuestions?.length) return null;

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {data.submissionQuestions.map((question) => (
        <Box
          key={question.id}
          sx={(theme) => ({
            background: theme.palette.highlight.neutral,
            borderRadius: '4px',
            padding: '16px 24px',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
          })}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            gap={3}
          >
            <Box display="flex" gap={1}>
              <ProfilePicture url={question.author.avatarURL} />
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="space-between"
              >
                <Typography>{question.author.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {question.author.role}
                </Typography>
              </Box>
            </Box>
            <Typography variant="body2" color="text.secondary">
              {format(new Date(question.createdAt), 'MM/dd/yyyy h:mm a')}
            </Typography>
          </Box>
          <Typography sx={{ whiteSpace: 'pre-line' }}>
            {question.text}
          </Typography>
        </Box>
      ))}
      {canUpdateCandidate && (
        <TextField
          label="Ask additional question"
          variant="outlined"
          fullWidth
          value={questionText}
          required
          onChange={(e) => setQuestionText(e.target.value)}
          disabled={isLoading}
          multiline
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton disabled={isLoading} onClick={onSubmitQuestion}>
                  <ReplyIcon sx={{ color: 'text.secondary' }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      )}
    </Box>
  );
};
